import React from 'react'
import { Box, Button, Typography } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

import { AppBrandMark, AppDialog, AppDialogBody, AppDialogCancel, AppDialogFooter, AppDialogHeader, AppNote, AskIrisButton, secondaryButtonSx } from '@components'
import { besidePanel } from '@components/AskIris/besidePanel'
import { pushRight } from '@app/@signals'
import { userData } from '@signals/user'
import { isIrisUser } from 'Layout/PmsAgentWidget'
import { BI_ROUTES } from '@constants'
import { dsColorNew } from '@palette'
import { money, ordinal, signedMoney } from '../../../@services/format'
import { pct, points, tone } from '@utils/colourScale'
import { cellInk } from './DistanceCell'
import { networkCellEvidence } from '../../../@services/askEvidence'


const tile = { border: `1px solid ${dsColorNew.grayBorder}`, borderRadius: '10px', p: 1.25, minWidth: 0 }
const tileTitle = { fontSize: 11, fontWeight: 600, letterSpacing: 0.6, textTransform: 'uppercase', color: dsColorNew.textSubtle }

function Tile({ title, children, sub }) {
	return <Box sx={tile}>
		<Typography sx={tileTitle}>{title}</Typography>
		<Box sx={{ fontSize: 18, fontWeight: 700, color: dsColorNew.textTitle, mt: 0.25 }}>{children}</Box>
		{!!sub && <Typography sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{sub}</Typography>}
	</Box>
}

/**
 * The detail of a cell (CA 20): your price, the market median and of how many competitors, your distance and its change,
 * your position, the cheapest and the dearest; every provider (or website) of the cell cheapest first, with the
 * median line, You and yours; the coverage of both periods; and the way to Season and to Advanced (BI).
 *
 * "Ask Iris" (CU-869fkdhjv phase 3, UX C10): in the header, left of the ×, about this office and column — your
 * distance to the market (the median's move without a brand). For Iris users the dialog stays open beside the panel
 * (`besidePanel`), only while there is something to ask (`askable`: not under the page's "Updating…" veil).
 */
export default function NetworkDetailDialog({ open, office, column, cell, mode, code, subtitle, compareText, perDay, onClose, onSeason, onExplorer, askable = false, askScope = null }) {
	if (!open || !office || !cell) return null
	const canAsk = askable && !cell.empty
	const withIris = canAsk && isIrisUser(userData.value)
	const getAsk = () => networkCellEvidence({ office, column, cell, mode, code, perDay, compareText, scope: askScope })
	const market = mode === 'market'
	const t = tone(cell.distance)
	const unit = perDay ? '/day' : ''
	const medianAt = cell.median === null ? -1 : cell.list.findIndex(e => e.v > cell.median)
	const youId = cell.you?.id
	const yours = new Set((cell.yours || []).map(e => e.id))
	const rows = []
	cell.list.forEach((e, i) => {
		if (i === medianAt) rows.push(<tr key="median" className="median"><td colSpan={market ? 3 : 4}>— Market median {money(cell.median, code)}{unit} —</td></tr>)
		const pill = e.id === youId ? 'You' : yours.has(e.id) ? 'Yours · not in the median' : null
		rows.push(<tr key={e.id} className={e.id === youId ? 'you' : undefined}>
			<td><Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>{mode !== 'site' && <AppBrandMark name={e.name} size={16}/>}{e.name}{pill && <Box component="span" sx={{ fontSize: 11, color: dsColorNew.appBlue, fontWeight: 600 }}>{pill}</Box>}</Box></td>
			<td style={{ textAlign: 'right' }}>{money(e.v, code)}{unit}</td>
			{!market && <td style={{ textAlign: 'right' }}>{cell.you && e.id !== youId ? signedMoney(e.v - cell.you.v, code) : ''}</td>}
			<td style={{ textAlign: 'right' }}>{e.n}</td>
		</tr>)
	})
	if (medianAt === -1 && cell.median !== null) rows.push(<tr key="median" className="median"><td colSpan={market ? 3 : 4}>— Market median {money(cell.median, code)}{unit} —</td></tr>)

	return <AppDialog open={open} onClose={onClose} maxWidth="md" aria-labelledby="network-detail-title" {...besidePanel({}, { withIris, right: pushRight.value })}>
		<AppDialogHeader icon="building" title={<span id="network-detail-title">{office.name} · {column.label}</span>} subtitle={subtitle}
			actions={canAsk ? <AskIrisButton variant="label" tooltip="Ask Iris about this office" getAsk={getAsk}/> : null} onClose={onClose}/>
		<AppDialogBody sx={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
			{cell.thin && <AppNote severity="warning" sx={{ mb: 1.5 }}>Few prices behind this cell: read it with care.</AppNote>}
			<Box sx={{ display: 'grid', gridTemplateColumns: market ? 'repeat(3, 1fr)' : 'repeat(4, 1fr)', gap: 1.25, mb: 1.5 }}>
				{!market && <Tile title="Your price" sub={cell.you?.name}>{cell.you ? `${money(cell.you.v, code)}${unit}` : '—'}</Tile>}
				<Tile title="Market median" sub={cell.median === null ? 'Not enough competitors' : `of ${cell.nCompetitors} ${market ? 'providers' : 'competitors, your brands excluded'}`}>{money(cell.median, code)}{cell.median !== null ? unit : ''}</Tile>
				{!market && <Tile title="Your distance" sub={cell.distance === null ? null : `${signedMoney(cell.distanceE, code)}${unit}${cell.change !== null ? ` · ${points(cell.change)} since ${compareText}` : ''}`}>
					<Box component="span" sx={{ color: cellInk(t) }}>{cell.distance === null ? '—' : `${t.arrow} ${pct(cell.distance)}`}</Box>
				</Tile>}
				{!market && <Tile title="Your position" sub="1 = the cheapest">{cell.position ? `${ordinal(cell.position)} of ${cell.total}` : '—'}</Tile>}
				{market && <Tile title="Change" sub={`since ${compareText}`}>{cell.marketChange === null ? 'no comparison' : pct(cell.marketChange)}</Tile>}
				{market && <Tile title="Prices" sub="behind the cell">{cell.n}</Tile>}
			</Box>
			<Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 2 }}>
				{[['Cheapest', cell.cheapest], ['Dearest', cell.dearest]].map(([title, e]) => <Box key={title} sx={tile}>
					<Typography sx={tileTitle}>{title}</Typography>
					{e ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
						{mode !== 'site' && <AppBrandMark name={e.name} size={18}/>}<b>{e.name}</b> {money(e.v, code)}{unit}
						{!market && cell.you && e.id !== cell.you.id && <Typography component="span" sx={{ fontSize: 12, color: dsColorNew.textMuted }}>{signedMoney(e.v - cell.you.v, code)} vs you</Typography>}
					</Box> : '—'}
				</Box>)}
			</Box>
			<Typography sx={{ fontSize: 13, fontWeight: 600, color: dsColorNew.textTitle, mb: 1 }}>{mode === 'site' ? 'Websites' : 'Providers'} in this cell, cheapest first</Typography>
			<Box component="table" sx={{
				width: '100%', borderCollapse: 'separate', borderSpacing: 0, fontSize: 13,
				'& th': { backgroundColor: dsColorNew.tableHead, color: 'rgba(255,255,255,.92)', fontWeight: 700, textAlign: 'right', px: 2, py: 1 },
				'& th:first-of-type': { textAlign: 'left' },
				'& td': { px: 2, py: 0.9, borderBottom: `1px solid ${dsColorNew.grayBorder}`, fontVariantNumeric: 'tabular-nums' },
				'& tr.median td': { textAlign: 'center', color: dsColorNew.textMuted, fontSize: 12, backgroundColor: '#FAFAFB' },
				'& tr.you td': { backgroundColor: '#F3F8FE' },
			}}>
				<thead><tr><th>{mode === 'site' ? 'Website' : 'Provider'}</th><th>Price</th>{!market && <th>vs you</th>}<th>Prices</th></tr></thead>
				<tbody>{rows}</tbody>
			</Box>
			<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1.5 }}>
				Now: {cell.n} prices{cell.you ? ` (${cell.you.n} yours)` : ''} · {compareText}: {cell.prevN === null ? 'no comparison' : `${cell.prevN} prices`}
			</Typography>
		</AppDialogBody>
		<AppDialogFooter>
			<Button variant="outlined" onClick={onSeason} sx={secondaryButtonSx}>Open in Season vs last year</Button>
			<Button variant="outlined" onClick={onExplorer} startIcon={<IconSeeker icon="sliders" sx={{ fontSize: 13 }}/>} sx={secondaryButtonSx}>Open in {BI_ROUTES.TITLE}</Button>
			<Box sx={{ flex: 1 }}/>
			<AppDialogCancel onClick={onClose}>Close</AppDialogCancel>
		</AppDialogFooter>
	</AppDialog>
}
